import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarIcon from '@mui/icons-material/Star';
import { Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { BookingStatus } from 'happypaws-shared/src/Booking/models/Booking';
import { SitterBookingWithDetails } from 'happypaws-shared/src/Booking/models/BookingWithDetails';
import { ServiceType, SERVICE_TYPE_LABELS } from 'happypaws-shared/src/Services/services';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetSitterProfile } from '../../../hooks/sitter/useGetSitterProfile';
import { ActivityType } from 'shared/constants';
import { Activity } from 'shared/entities';
import { useGetSitterBookings } from '../../../hooks/bookings/useGetSitterBookings';
import { useUpdateBookingStatus } from '../../../hooks/bookings/useUpdateBookingStatus';
import ActivityFeed from './components/ActivityFeed';
import BookingRequestCard from './components/BookingRequestCard';
import StatCard from './components/StatCard';
import UpcomingBookingCard from './components/UpcomingBookingCard';

import SitterHeader from '../SitterHeader/SitterHeader';

const SitterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: bookings } = useGetSitterBookings();
  const { data: sitterProfile } = useGetSitterProfile();

  const isNotOnboarded =
    sitterProfile !== undefined &&
    !sitterProfile.bio &&
    (!sitterProfile.serviceTypes || sitterProfile.serviceTypes.length === 0);

  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  const pendingBookings = useMemo(
    () => bookings?.filter((b) => b.status === BookingStatus.PENDING) ?? [],
    [bookings]
  );

  const upcomingBookings = useMemo(() => {
    if (!bookings) return [];
    const startOfWeek = dayjs().startOf('week');
    const endOfWeek = dayjs().endOf('week');
    return bookings.filter((b) => {
      const start = dayjs(b.startDate);
      return (
        b.status === BookingStatus.CONFIRMED &&
        (start.isAfter(startOfWeek) || start.isSame(startOfWeek)) &&
        (start.isBefore(endOfWeek) || start.isSame(endOfWeek))
      );
    });
  }, [bookings]);

  const weeklyEarnings = useMemo(() => {
    if (!bookings) return 0;
    const startOfWeek = dayjs().startOf('week');
    return bookings
      .filter((b) => {
        return b.status === BookingStatus.COMPLETED && dayjs(b.startDate).isAfter(startOfWeek);
      })
      .reduce((sum, b) => sum + Number(b.totalPrice), 0);
  }, [bookings]);

  const totalBookings = bookings?.length ?? 0;

  // Mock activity — no real activity system yet
  const recentActivities: Activity[] = [
    {
      id: '1',
      type: ActivityType.BOOKING,
      title: 'Booking completed: Luna',
      description: 'Payment of $120 received',
      timestamp: '2 hours ago',
    },
    {
      id: '2',
      type: ActivityType.REVIEW,
      title: 'New review from Michael B.',
      description: '"Excellent care for Luna!"',
      timestamp: '5 hours ago',
      rating: 5,
    },
    {
      id: '3',
      type: ActivityType.PROFILE_VIEW,
      title: 'Profile viewed 12 times',
      description: 'Your profile is trending!',
      timestamp: '1 day ago',
    },
  ];

  const { mutate: updateStatus } = useUpdateBookingStatus();

  const handleAccept = (id: string) => {
    updateStatus({ bookingId: id, status: BookingStatus.CONFIRMED });
  };

  const handleDecline = (id: string) => {
    updateStatus({ bookingId: id, status: BookingStatus.CANCELLED });
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    return `${dayjs(startDate).format('MMM D')} - ${dayjs(endDate).format('MMM D, YYYY')}`;
  };

  const formatDuration = (startDate: string, endDate: string) => {
    const days = dayjs(endDate).diff(dayjs(startDate), 'day');
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <SitterHeader />

      <Dialog open={isNotOnboarded && !onboardingDismissed} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Complete your profile</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#5A5A5A' }}>
            Before clients can find you, you need to set up your sitter profile — add your bio,
            services, and availability.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOnboardingDismissed(true)} color="inherit">
            Later
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/boarding-sitters')}
            sx={{ backgroundColor: '#178B8E', '&:hover': { backgroundColor: '#0F6164' } }}
          >
            Set up profile
          </Button>
        </DialogActions>
      </Dialog>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
          Dashboard
        </Typography>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Pending Requests"
              value={pendingBookings.length}
              change={`${pendingBookings.length} awaiting response`}
              changeColor="success"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="This Week's Earnings"
              value={`$${weeklyEarnings}`}
              change="From completed bookings"
              changeColor="success"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Your Rating"
              value="New"
              change="No reviews yet"
              changeColor="info"
              icon={<StarIcon sx={{ fontSize: 32, color: '#FDB833' }} />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Total Bookings"
              value={totalBookings}
              change="All time"
              changeColor="info"
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            {/* Pending Requests */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Pending Requests
              </Typography>
              {pendingBookings.length > 0 && (
                <Chip
                  label={`${pendingBookings.length} New`}
                  size="small"
                  sx={{
                    backgroundColor: 'error.main',
                    color: 'white',
                    fontWeight: 600,
                    borderRadius: '12px',
                  }}
                />
              )}
            </Box>

            {pendingBookings.length === 0 ? (
              <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No pending requests</Typography>
              </Paper>
            ) : (
              <Box>
                {pendingBookings.map((booking: SitterBookingWithDetails) => (
                  <BookingRequestCard
                    key={booking.id}
                    ownerName={`${booking.clientFirstName} ${booking.clientLastName}`}
                    petInfo={booking.pets.map((p) => p.petName).join(', ')}
                    serviceType={
                      SERVICE_TYPE_LABELS[booking.serviceType as ServiceType] ?? booking.serviceType
                    }
                    dateRange={formatDateRange(booking.startDate, booking.endDate)}
                    duration={formatDuration(booking.startDate, booking.endDate)}
                    price={Number(booking.totalPrice)}
                    onAccept={() => handleAccept(booking.id)}
                    onDecline={() => handleDecline(booking.id)}
                  />
                ))}
              </Box>
            )}

            {/* Upcoming This Week */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Upcoming This Week
              </Typography>
              {upcomingBookings.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">No upcoming bookings this week</Typography>
                </Paper>
              ) : (
                upcomingBookings.map((booking: SitterBookingWithDetails) => (
                  <UpcomingBookingCard
                    key={booking.id}
                    petName={booking.pets.map((p) => p.petName).join(', ')}
                    petInitial={booking.pets[0]?.petName?.charAt(0) ?? 'P'}
                    petBreed=""
                    ownerName={`${booking.clientFirstName} ${booking.clientLastName}`}
                    dateRange={formatDateRange(booking.startDate, booking.endDate)}
                    serviceType={
                      SERVICE_TYPE_LABELS[booking.serviceType as ServiceType] ?? booking.serviceType
                    }
                    price={Number(booking.totalPrice)}
                    onViewDetails={() => navigate('/sitter-bookings')}
                  />
                ))
              )}
            </Box>
          </Grid>

          {/* Right Sidebar */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper
              sx={{
                p: 3,
                mb: 3,
                textAlign: 'center',
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Availability Calendar
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CalendarMonthIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click dates to set availability
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/sitter-calendar')}
              >
                Manage Availability
              </Button>
            </Paper>

            <ActivityFeed activities={recentActivities} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SitterDashboard;
